import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react'

import { Dialog, Transition } from '@headlessui/react'
import { cx } from '@linaria/core'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useFoodSWR, useFoodSWRInfinite } from '~/api-client/food_api'
import {
  ChatSpaceMember,
  ChatSpaceUser,
  DinnerMember,
  User,
} from '~/api-definition/definition'
import {
  ChatSpacePermissionType,
  DinnerMemberType,
} from '~/api-definition/enumTypes'
import { Button } from '~/components/action/Button'
import { AppImage } from '~/components/data-display/AppImage'
import { Checkbox } from '~/components/data-input/Checkbox'
import { SelectV2 } from '~/components/data-input/SelectV2'
import { TextField } from '~/components/data-input/TextField'
import {
  ChevronLeftIcon,
  CrownIcon,
  DeleteIcon,
  SearchIcon,
} from '~/components/icons'
import { Container } from '~/components/layout/Container'
import { Header, headerHeight } from '~/components/layout/Header'
import { config } from '~/config'
import { MinimumDinnerMember, MinimumDinnerUser } from '~/libs/dinner'
import { hasMorePage, nextPage, offsetPager } from '~/libs/paging'
import { userProfileIcon } from '~/libs/user'
import { useSession } from '~/provider/SessionProvider'

export const MemberSelectModal: React.FC<
  | {
      target: 'DinnerMember'
      title: string
      primaryButtonText?: string
      isOpen: boolean
      onSubmit: () => void
      onCancel: () => void
      memberTypes: { label: string; value: DinnerMemberType }[]
      members: MinimumDinnerMember[]
      membersAdded: MinimumDinnerMember[]
      onMemberChanged: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberAdded: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberDeleted: (user: MinimumDinnerUser) => void
    }
  | {
      target: 'ChatSpaceMember'
      title: string
      primaryButtonText?: string
      isOpen: boolean
      onSubmit: () => void
      onCancel: () => void
      memberTypes: {
        label: string
        value: ChatSpacePermissionType
      }[]
      members: ChatSpaceMember[]
      membersAdded: ChatSpaceMember[]
      onMemberChanged: (
        user: ChatSpaceUser,
        memberType: ChatSpacePermissionType
      ) => void
      onMemberAdded: (
        user: ChatSpaceUser,
        memberType: ChatSpacePermissionType
      ) => void
      onMemberDeleted: (user: ChatSpaceUser) => void
    }
> = (props) => {
  return (
    <Transition appear show={props.isOpen} as={Fragment}>
      <Dialog
        as="div"
        onClose={() => {
          /* 無視する */
        }}
      >
        <div className="fixed inset-0" style={{ zIndex: 999 }}>
          <div className="flex items-center justify-center text-center">
            <Transition.Child as={Fragment}>
              <Dialog.Panel className="h-[100svh] w-full transform overflow-hidden bg-white">
                <MemberSelectList
                  {...props}
                  withoutFooter
                  onSubmit={props.onSubmit}
                  onCancel={props.onCancel}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export const MemberSelectList: React.FC<
  | {
      target: 'DinnerMember'
      title: string
      primaryButtonText?: string
      ignoreUserIds?: string[]
      memberTypes: { label: string; value: DinnerMemberType }[]
      members: MinimumDinnerMember[]
      membersAdded: MinimumDinnerMember[]
      onMemberChanged: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberAdded: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberDeleted: (user: MinimumDinnerUser) => void
      onSubmit: () => void
      onCancel: () => void
      withoutFooter?: boolean
    }
  | {
      target: 'ChatSpaceMember'
      title: string
      primaryButtonText?: string
      ignoreUserIds?: string[]
      memberTypes: {
        label: string
        value: ChatSpacePermissionType
      }[]
      members: Pick<ChatSpaceMember, 'user' | 'permissionType'>[]
      membersAdded: Pick<ChatSpaceMember, 'user' | 'permissionType'>[]
      onMemberChanged: (
        user: ChatSpaceUser,
        ChatSpaceUser: ChatSpacePermissionType
      ) => void
      onMemberAdded: (
        user: ChatSpaceUser,
        memberType: ChatSpacePermissionType
      ) => void
      onMemberDeleted: (user: ChatSpaceUser) => void
      onSubmit: () => void
      onCancel: () => void
      withoutFooter?: boolean
    }
> = (props) => {
  const session = useSession()
  const disabled =
    session.response == null ||
    (
      props.membersAdded as (
        | DinnerMember
        | Pick<ChatSpaceMember, 'user' | 'permissionType'>
      )[]
    ).filter((member) => member.user.userId !== session.response?.data.userId)
      .length < 1
  const [changeMode, setChangeMode] = useState(false)

  const handleBack = () => {
    if (changeMode) {
      setChangeMode(false)
    } else {
      props.onCancel()
    }
  }
  const handleClick = () => {
    if (changeMode) {
      setChangeMode(false)
    } else {
      props.onSubmit()
    }
  }
  return (
    <>
      <Header>
        <div className="w-5">
          <button type="button" onClick={handleBack} tabIndex={-1}>
            <ChevronLeftIcon fill="#fff" />
          </button>
        </div>
        {props.title}
        <div className="w-5" />
      </Header>
      <div className={cx(!changeMode && 'hidden')}>
        <MemberConfigure {...props} setChangeMode={setChangeMode} />
      </div>
      <div className={cx(changeMode && 'hidden')}>
        <MemberPicker {...props} setChangeMode={setChangeMode} />
      </div>
      <div
        className={cx(
          'fixed h-[120px] flex items-center justify-center space-x-4 bottom-0 w-full',
          props.withoutFooter ? 'bottom-0' : 'bottom-[var(--footer-height)]'
        )}
      >
        <Button
          variant="primary"
          padding="9px 18px"
          disabled={disabled}
          onClick={handleClick}
        >
          {!changeMode && props.primaryButtonText != null
            ? props.primaryButtonText
            : '決定する'}
        </Button>
      </div>
    </>
  )
}

const buttonHeightPx = 40
const buttonMarginYPx = 32

export const MemberPicker: React.FC<
  | {
      target: 'DinnerMember'
      ignoreUserIds?: string[]
      memberTypes: { label: string; value: DinnerMemberType }[]
      members: { user: MinimumDinnerUser; memberType: DinnerMemberType }[]
      membersAdded: { user: MinimumDinnerUser; memberType: DinnerMemberType }[]
      onMemberChanged: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberAdded: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberDeleted: (user: MinimumDinnerUser) => void
      setChangeMode: (v: boolean) => void
      withoutFooter?: boolean
    }
  | {
      target: 'ChatSpaceMember'
      ignoreUserIds?: string[]
      memberTypes: {
        label: string
        value: ChatSpacePermissionType
      }[]
      members: Pick<ChatSpaceMember, 'user' | 'permissionType'>[]
      membersAdded: Pick<ChatSpaceMember, 'user' | 'permissionType'>[]
      onMemberChanged: (
        user: ChatSpaceUser,
        memberType: ChatSpacePermissionType
      ) => void
      onMemberAdded: (
        user: ChatSpaceUser,
        memberType: ChatSpacePermissionType
      ) => void
      onMemberDeleted: (user: ChatSpaceUser) => void
      setChangeMode: (v: boolean) => void
      withoutFooter?: boolean
    }
> = (props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const searchQueryInputRef = useRef<HTMLInputElement | null>(null)
  const onChangeSearchQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )

  const session = useSession()

  const selectedMembers = [...props.members].concat(props.membersAdded)

  const searchUsers = useFoodSWRInfinite(
    '/user/search_users',
    { key: searchQuery },
    offsetPager(config.pageSize.userSearchList),
    {
      disabled: !searchQuery,
    }
  )

  const dinnerAttendees = useFoodSWR('/user/dinner_attendees', {})

  const listAreaRef = useRef<HTMLDivElement | null>(null)

  const updateListAreaHeight = useCallback(
    (fixedAreaNode: HTMLDivElement | null) => {
      if (fixedAreaNode) {
        if (listAreaRef.current) {
          listAreaRef.current.style.height = `calc((100svh - ${
            window.getComputedStyle(fixedAreaNode).height
          } - ${headerHeight} - ${
            props.withoutFooter ? '0px' : 'var(--footer-height)'
          }))`
        }
      }
    },
    [props.withoutFooter]
  )

  const handleFixedArea = useCallback(
    (node: HTMLDivElement | null) => {
      if (node != null) {
        const observer = new ResizeObserver(() => {
          updateListAreaHeight(node)
        })
        observer.observe(node)
        updateListAreaHeight(node)
      }
    },
    [updateListAreaHeight]
  )

  const candicates = useMemo(() => {
    const flattenCandidates: User[] = []
    searchUsers.data?.forEach((response) =>
      response.users.forEach((user) => {
        if (
          props.ignoreUserIds?.includes(user.userId) ||
          user.userId === session.response?.data.userId
        ) {
          return
        }
        flattenCandidates.push(user)
      })
    )
    return flattenCandidates
  }, [props.ignoreUserIds, searchUsers.data, session.response?.data.userId])

  const attendees = useMemo(() => {
    return (
      dinnerAttendees.data?.attendees.filter((user) => {
        if (props.ignoreUserIds?.includes(user.user.userId)) {
          return false
        }
        return true
      }) ?? []
    )
  }, [dinnerAttendees.data?.attendees, props.ignoreUserIds])

  return (
    <div>
      <div ref={handleFixedArea}>
        <Container>
          <div className="mx-4 md:mx-0 py-4">
            <div className="flex items-center">
              <TextField
                leftIcon={<SearchIcon className="w-4 h-4 fill-text" />}
                inputProps={{
                  ref: searchQueryInputRef,
                  onChange: onChangeSearchQuery,
                  placeholder: '名前、ユーザーIDから検索する',
                }}
              />
            </div>
          </div>
        </Container>

        {selectedMembers.length > 0 && (
          <div>
            <div className="bg-[#f4f4f4] px-4 py-2">
              <div className="max-w-screen-md mx-auto w-full">
                <div className="grid grid-cols-6 gap-y-2 py-2">
                  {selectedMembers.map((member) => {
                    const showCrown =
                      'permissionType' in member
                        ? member.permissionType === 'manager'
                        : member.memberType === 'co_organizer'
                    return (
                      <div
                        key={member.user.userId}
                        className="flex flex-col gap-y-1 items-center"
                      >
                        {showCrown ? (
                          <CrownIcon className="fill-text h-[14px] w-[18px]" />
                        ) : (
                          <div className="h-[14px]" />
                        )}
                        <div className="relative">
                          {member.user.userId !==
                            session.response?.data.userId && (
                            <button
                              type="button"
                              onClick={() => props.onMemberDeleted(member.user)}
                              className="absolute top-0 -right-2 z-10"
                            >
                              <DeleteIcon className="h-4 w-4 fill-text" />
                            </button>
                          )}
                          <AppImage
                            variant={{
                              type: 'profile',
                              user: member.user,
                            }}
                            rounded="full"
                            width={50}
                            height={50}
                            className="rounded-full"
                            alt={`${member.user.displayName}さんのプロフィール画像`}
                            src={userProfileIcon(member.user)}
                          />
                        </div>
                        <div className="text-[#C2C2C2] text-[13px] truncate max-w-full">
                          {member.user.displayName}
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => props.setChangeMode(true)}
                    className="text-[13px] underline"
                  >
                    役割を変更する
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {candicates.length === 0 && (
          <Container>
            <div className="mx-4 md:mx-0 py-3">
              <p className="text-sm px-5 text-left">
                ユーザーを検索し、右横にあるチェックボックスにチェックを入れると追加されます。
              </p>
            </div>
          </Container>
        )}
      </div>

      <Container>
        <InfiniteScroll
          height={`calc(100svh - ${headerHeight} - var(--footer-height) - 30px)`}
          dataLength={candicates?.length ?? 0}
          next={() => nextPage(searchUsers)}
          hasMore={hasMorePage(searchUsers)}
          loader={<div className="loader" key={0} />}
        >
          <div
            className="flex flex-col gap-y-3 overflow-y-auto px-4 md:px-0"
            ref={listAreaRef}
            style={{ paddingBottom: buttonHeightPx + buttonMarginYPx * 2 }}
          >
            {candicates.length === 0 && (
              <>
                <div className="font-bold text-left text-md mt-4">
                  同席回数が多いフーディー
                </div>
                {attendees.map((attendee) => {
                  switch (props.target) {
                    case 'ChatSpaceMember':
                      return (
                        <div key={attendee.user.userId}>
                          <MemberCheckRow
                            target={props.target}
                            user={attendee.user}
                            memberType={
                              props.membersAdded.find(
                                (m) => m.user.userId === attendee.user.userId
                              )?.permissionType
                            }
                            onChangeMemberType={props.onMemberAdded}
                          />
                        </div>
                      )
                    case 'DinnerMember':
                      return (
                        <div key={attendee.user.userId}>
                          <MemberCheckRow
                            target={props.target}
                            onChangeMemberType={props.onMemberAdded}
                            onDeleteMember={props.onMemberDeleted}
                            user={attendee.user}
                            memberType={
                              props.membersAdded.find(
                                (m) => m.user.userId === attendee.user.userId
                              )?.memberType
                            }
                          />
                        </div>
                      )
                  }
                })}
              </>
            )}

            {candicates.map((user) => {
              switch (props.target) {
                case 'ChatSpaceMember':
                  return (
                    <div key={user.userId}>
                      <MemberCheckRow
                        target={props.target}
                        user={user}
                        memberType={
                          props.membersAdded.find(
                            (m) => m.user.userId === user.userId
                          )?.permissionType
                        }
                        onChangeMemberType={props.onMemberAdded}
                      />
                    </div>
                  )
                case 'DinnerMember':
                  return (
                    <div key={user.userId}>
                      <MemberCheckRow
                        target={props.target}
                        onChangeMemberType={props.onMemberAdded}
                        onDeleteMember={props.onMemberDeleted}
                        user={user}
                        memberType={
                          props.membersAdded.find(
                            (m) => m.user.userId === user.userId
                          )?.memberType
                        }
                      />
                    </div>
                  )
              }
            })}
          </div>
        </InfiniteScroll>
      </Container>
    </div>
  )
}

export const MemberConfigure: React.FC<
  | {
      target: 'DinnerMember'
      ignoreUserIds?: string[]
      memberTypes: { label: string; value: DinnerMemberType }[]
      members: { user: MinimumDinnerUser; memberType: DinnerMemberType }[]
      membersAdded: { user: MinimumDinnerUser; memberType: DinnerMemberType }[]
      onMemberChanged: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberAdded: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onMemberDeleted: (user: MinimumDinnerUser) => void
      setChangeMode: (v: boolean) => void
      withoutFooter?: boolean
    }
  | {
      target: 'ChatSpaceMember'
      ignoreUserIds?: string[]
      memberTypes: {
        label: string
        value: ChatSpacePermissionType
      }[]
      members: Pick<ChatSpaceMember, 'user' | 'permissionType'>[]
      membersAdded: Pick<ChatSpaceMember, 'user' | 'permissionType'>[]
      onMemberChanged: (
        user: ChatSpaceUser,
        memberType: ChatSpacePermissionType
      ) => void
      onMemberAdded: (
        user: ChatSpaceUser,
        memberType: ChatSpacePermissionType
      ) => void
      onMemberDeleted: (user: ChatSpaceUser) => void
      setChangeMode: (v: boolean) => void
      withoutFooter?: boolean
    }
> = (props) => {
  const session = useSession()

  return (
    <div>
      <Container>
        <div className="mx-4 my-3 md:mx-0">
          <div className="flex flex-col gap-y-3">
            {(() => {
              switch (props.target) {
                case 'ChatSpaceMember': {
                  const selectedMembers = [...props.members].concat(
                    props.membersAdded
                  )

                  return selectedMembers.map((member) => {
                    return (
                      <div key={member.user.userId}>
                        <MemberSelectRow
                          target={props.target}
                          user={member.user}
                          memberTypes={props.memberTypes}
                          memberType={member.permissionType}
                          onChangeMemberType={props.onMemberChanged}
                          onDeleteMember={props.onMemberDeleted}
                          disabled={
                            member.user.userId === session.response?.data.userId
                          }
                        />
                      </div>
                    )
                  })
                }
                case 'DinnerMember': {
                  const selectedMembers = [...props.members]
                    .concat(props.membersAdded)
                    .filter(
                      (member) =>
                        member.user.userId !== session.response?.data.userId
                    )

                  return selectedMembers.map((member) => {
                    return (
                      <div key={member.user.userId}>
                        <MemberSelectRow
                          target={props.target}
                          memberTypes={props.memberTypes}
                          onChangeMemberType={props.onMemberChanged}
                          user={member.user}
                          memberType={member.memberType}
                        />
                      </div>
                    )
                  })
                }
              }
            })()}
          </div>
        </div>
      </Container>
    </div>
  )
}

const MemberCheckRow: React.FC<
  | {
      target: 'DinnerMember'
      user: MinimumDinnerUser
      memberType?: DinnerMemberType
      onChangeMemberType: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      onDeleteMember?: (user: MinimumDinnerUser) => void
      disabled?: boolean
    }
  | {
      target: 'ChatSpaceMember'
      user: ChatSpaceUser
      memberType?: ChatSpacePermissionType
      onChangeMemberType: (
        user: ChatSpaceUser,
        permissionType: ChatSpacePermissionType
      ) => void
      onDeleteMember?: (user: ChatSpaceUser) => void
      disabled?: boolean
    }
> = (props) => {
  return (
    <div className="flex gap-4 text-left w-full">
      <div className="min-w-[54px]">
        <AppImage
          variant={{ type: 'profile', user: props.user }}
          rounded="full"
          width={50}
          height={50}
          className="rounded-full"
          alt={`プロフィール画像`}
          src={userProfileIcon(props.user)}
        />
      </div>
      <div className="flex-1 flex items-center justify-between">
        <div style={{ width: `calc(100% - 160px)` }} className="flex-1">
          <div className="font-bold text-sm overflow-hidden overflow-ellipsis whitespace-nowrap">
            {props.user.displayName}
          </div>
          <div className="text-sm truncate max-w-[120px]">
            @{props.user.userName}
          </div>
        </div>

        <div className="flex gap-x-2 relative">
          <Checkbox
            name="user-check"
            id={`user-check-${props.user.userId}`}
            checked={props.memberType != null}
            onCheckedChange={(v) => {
              if (v === 'indeterminate') return
              if (v) {
                if (props.target === 'DinnerMember') {
                  props.onChangeMemberType(props.user, 'attendee')
                } else {
                  props.onChangeMemberType(props.user, 'member')
                }
              } else {
                if (props.onDeleteMember) {
                  props.onDeleteMember(props.user)
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}

const MemberSelectRow: React.FC<
  | {
      target: 'DinnerMember'
      user: MinimumDinnerUser
      memberTypes: { label: string; value: DinnerMemberType }[]
      memberType?: DinnerMemberType
      onChangeMemberType: (
        user: MinimumDinnerUser,
        memberType: DinnerMemberType
      ) => void
      disabled?: boolean
    }
  | {
      target: 'ChatSpaceMember'
      user: ChatSpaceUser
      memberTypes: {
        label: string
        value: ChatSpacePermissionType
      }[]
      memberType?: ChatSpacePermissionType
      onChangeMemberType: (
        user: ChatSpaceUser,
        permissionType: ChatSpacePermissionType
      ) => void
      onDeleteMember?: (user: ChatSpaceUser) => void
      disabled?: boolean
    }
> = (props) => {
  const options = [
    ...(props.memberType == null ? [{ value: '', label: '選択' }] : []),
    ...props.memberTypes,
  ]
  return (
    <div className="flex gap-4 text-left w-full">
      <div className="min-w-[54px]">
        <AppImage
          variant={{ type: 'profile', user: props.user }}
          rounded="full"
          width={50}
          height={50}
          className="rounded-full"
          alt={`プロフィール画像`}
          src={userProfileIcon(props.user)}
        />
      </div>
      <div className="flex-1 flex items-center justify-between">
        <div style={{ width: `calc(100% - 160px)` }} className="flex-1">
          <div className="font-bold text-sm overflow-hidden overflow-ellipsis whitespace-nowrap">
            {props.user.displayName}
          </div>
          <div className="text-sm truncate max-w-[120px]">
            @{props.user.userName}
          </div>
        </div>

        <div className="flex gap-x-2 relative">
          <SelectV2
            name="member_type"
            options={options}
            placeholder="選択"
            defaultValue={props.memberType ?? ''}
            disabled={props.disabled}
            width={110}
            onChange={(value) => {
              if (value === 'delete') {
                return
              }
              switch (props.target) {
                case 'DinnerMember':
                  for (const t of props.memberTypes) {
                    if (t.value === value) {
                      props.onChangeMemberType(props.user, t.value)
                      break
                    }
                  }
                  break
                case 'ChatSpaceMember':
                  for (const t of props.memberTypes) {
                    if (t.value === value) {
                      props.onChangeMemberType(props.user, t.value)
                      break
                    }
                  }
                  break
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
